import { Layout, Menu } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import OMSErrorBoundary from 'src/components/shared/ErrorBoundary'
import { SupplierInvitesCountsProvider } from 'src/context/SupplierInvitesCountsContext'
import { hasBrandAdminAccess, isOmsPlatformAdmin } from 'src/utils/adminAccess'
import {
  BRIDGE_ADMIN_LABEL,
  BRIDGE_ADMIN_PATH,
  BRIDGE_ADMIN_TAB_KEY,
  shouldShowBridgeAdminTab,
} from 'src/utils/bridgeAdminNav'
import {
  SupplierInvitesNavLabel,
  fetchSupplierInvitesCounts,
} from 'src/utils/supplierInvitesCounts'
import styled from 'styled-components/macro'
import { API } from 'src/utils/interceptor'

const { Item } = Menu

const AdminRoute = ({ children, isAdmin, supplierAdmin }) => {
  const location = useLocation()
  const username = useSelector(state => state.auth.cognito.username)
  const account = useSelector(state => state.auth.account)
  const [pendingCount, setPendingCount] = useState(0)
  const [defaultSuppliersCount, setDefaultSuppliersCount] = useState(0)
  const pathFragments = location.pathname.split('/')
  const hasSubMenu = false
  const slug = hasSubMenu
    ? pathFragments[pathFragments.length - 2]
    : pathFragments[pathFragments.length - 1]
  const brandAdminAccess = hasBrandAdminAccess(account)
  const showBridgeTab = shouldShowBridgeAdminTab(account)
  const includeDefaultSuppliers = isOmsPlatformAdmin(account)

  const refreshSupplierInvitesCounts = useCallback(async () => {
    if (!username) {
      setPendingCount(0)
      setDefaultSuppliersCount(0)
      return
    }

    try {
      const counts = await fetchSupplierInvitesCounts({
        username,
        apiGet: API.get,
        includeDefaultSuppliers,
      })
      setPendingCount(counts.pendingCount)
      setDefaultSuppliersCount(counts.defaultSuppliersCount)
    } catch (error) {
      console.error('Error fetching supplier invite counts:', error)
    }
  }, [username, includeDefaultSuppliers])

  useEffect(() => {
    if (isAdmin && username) {
      refreshSupplierInvitesCounts()
    }
  }, [isAdmin, username, refreshSupplierInvitesCounts])

  useEffect(() => {
    if (slug === 'supplier-invites' && username) {
      refreshSupplierInvitesCounts()
    }
  }, [slug, username, refreshSupplierInvitesCounts])

  const supplierInvitesCountsValue = useMemo(
    () => ({
      pendingCount,
      defaultSuppliersCount,
      refreshCounts: refreshSupplierInvitesCounts,
    }),
    [pendingCount, defaultSuppliersCount, refreshSupplierInvitesCounts],
  )

  return isAdmin ? (
    <>
      <StyledMenu mode='horizontal' selectedKeys={[slug]}>
        <Item key='orders'>
          <Link to={`/admin/orders`}>Orders</Link>
        </Item>
        <Item key='spending'>
          <Link to={`/admin/spending`}>Spending</Link>
        </Item>
        <Item key='products'>
          <Link to={`/admin/products`}>Products</Link>
        </Item>
        <Item key='suppliers'>
          <Link to={`/admin/suppliers`}>Suppliers</Link>
        </Item>
        <Item key='ironweb-catalogues'>
          <Link to={`/admin/ironweb-catalogues`}>IronWeb</Link>
        </Item>
        <Item key='supplier-invites'>
          <Link to={`/admin/supplier-invites`}>
            <SupplierInvitesNavLabel
              pendingCount={pendingCount}
              defaultSuppliersCount={defaultSuppliersCount}
            />
          </Link>
        </Item>
        <Item key='accounts'>
          <Link to={`/admin/accounts`}>Accounts</Link>
        </Item>
        {showBridgeTab && (
          <Item key={BRIDGE_ADMIN_TAB_KEY}>
            <Link to={BRIDGE_ADMIN_PATH}>{BRIDGE_ADMIN_LABEL}</Link>
          </Item>
        )}
        {!brandAdminAccess && (
          <Item key='invoice-exceptions'>
            <Link to={`/admin/invoice-exceptions`}>Invoice Exceptions</Link>
          </Item>
        )}
        <Item key='product-issues'>
          <Link to={`/admin/product-issues`}>Product Issues</Link>
        </Item>
      </StyledMenu>
      <StyledLayout hassubmenu={hasSubMenu ? 1 : 0}>
        <SupplierInvitesCountsProvider value={supplierInvitesCountsValue}>
          <OMSErrorBoundary>{children}</OMSErrorBoundary>
        </SupplierInvitesCountsProvider>
      </StyledLayout>
    </>
  ) : supplierAdmin ? (
    <>
      <StyledMenu mode='horizontal' selectedKeys={[slug]}>
        <Item key='orders'>
          <Link to={`/supplier/orders`}>Orders</Link>
        </Item>
        {/* <Item key='spending'>
          <Link to={`/supplier/spending`}>Spending</Link>
        </Item> */}
        <Item key='products'>
          <Link to={`/supplier/products`}>Products</Link>
        </Item>
      </StyledMenu>
      <StyledLayout hassubmenu={hasSubMenu ? 1 : 0}>
        <OMSErrorBoundary>{children}</OMSErrorBoundary>
      </StyledLayout>
    </>
  ) : null
}

const StyledMenu = styled(Menu)`
  height: 52px;
  background: ${({ theme }) => theme.colours.mayoWhite};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const StyledLayout = styled(Layout)`
  background: ${({ theme }) => theme.colours.mayoWhite} !important;
  min-height: calc(100vh - 167px) !important;
  overflow: visible;
  padding: ${({ theme, hassubmenu }) =>
    hassubmenu ? 0 : `${theme.spacing.lg} ${theme.spacing.xxl}`};
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: ${({ theme, hassubmenu }) =>
      hassubmenu ? 0 : `${theme.spacing.lg} ${theme.spacing.md}`};
  }
`

export default AdminRoute
